import {
  OnboardingWebsiteDetails,
  TaglineResponse,
  TaglineResponseType
} from "../types";
import { ChatMessage, FieldToComplete, PromptInfo } from "@/lib/ai/types";
import { Either, fold } from "fp-ts/lib/Either";
import { _Error } from "@/lib/wordpress/data";
import { getBarebonesSectionLibrary } from "./barebonesLibrary";
import { Sitemap, SitemapV } from "../types";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import fpJsonParseTypesafe from "@/util/fp-json-parse-typesafe";
import * as t from "io-ts";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import { pipe } from "fp-ts/lib/function";
import {
  cleanJson,
  cleanJsonBeforeParse,
  getTextFromChunk
} from "@/lib/ai/api";
import { jsonrepair } from "jsonrepair";
import { Tree, TreeNode } from "@/types/tree";

import {
  getElementDescription,
  getElementQualifier,
  getElementTemperature,
  getFieldsToComplete,
  handleAiError,
  processCompletion,
  saveToElementMetadata,
  updateDocument,
  getLockedFieldsPath,
  getElementPrompt,
  getImagesToComplete,
  updateImages
} from "@/lib/ai/ai";
import { formatFieldsForOpenAI, getPrompt } from "@/lib/ai/prompts";
import {
  documentStore,
  globalStore,
  onboardingStore,
  uiStore
} from "@/store/store";
import { findNode } from "@/lib/tree/tree";
import { breakdanceStoreActions } from "@/store/actions/store-actions";
import { OnMountedElement } from "@/types/store-actions";
import { SECTION_SLUG } from "@/lib/elements/constants";
import fpJsonParse from "@/util/fp-json-parse";
import { jsonStringifyOrUndefined } from "@/util/json-stringify-or-undefined";
import { callImageService } from "@/futurelayer/api/image-bridge";

export function ioTsToJsonSchema(type: t.Any): any {
  if (type === t.string) return { type: "string" };
  if (type === t.number) return { type: "number" };
  if (type instanceof t.ArrayType)
    return { type: "array", items: ioTsToJsonSchema(type.type) };

  if (type instanceof t.InterfaceType) {
    return {
      type: "object",
      properties: Object.fromEntries(
        Object.entries(type.props).map(([key, value]) => [
          key,
          ioTsToJsonSchema(value as t.Any)
        ])
      ),
      required: Object.keys(type.props),
      additionalProperties: false
    };
  }

  throw new Error(`Unsupported io-ts type: ${type.name}`);
}

export function ioTsToOpenAiJsonSchemaResponseFormatValue(schema: t.Any, name: string) {
  return {
    type: "json_schema",
    json_schema: {
      name: name,
      strict: true,
      schema: ioTsToJsonSchema(schema)
    }
  };
}

export function getWebsiteDetailsAsQualifier(
  websiteDetails: OnboardingWebsiteDetails
) {
  const metadata = [
    {
      name: "Website Name",
      value: websiteDetails.websiteName
    },
    {
      name: "Website Description",
      value: websiteDetails.websiteDescription
    },
    {
      name: "Website Category",
      value: websiteDetails.websiteCategory
        .map(category => category.label)
        .join(", ")
    },
    {
      name: "Website Goals",
      value: websiteDetails.websiteGoals.map(goal => goal.label).join(", ")
    },
    {
      name: "Website Writing Style",
      value: websiteDetails.websiteWritingStyle
        .map(personality => personality.label)
        .join(", ")
    }
  ];

  return metadata.reduce((acc, curr) => {
    if (!curr.value) return acc;
    return `${acc}\n${curr.name}: ${curr.value}`;
  }, "");
}

export function getWebsiteCategoryGoalsWritingStyleAsQualifier(
  websiteDetails: OnboardingWebsiteDetails
): string {
  const category = websiteDetails.websiteCategory
    .map(category => category.label)
    .join(", ");
  const goals = websiteDetails.websiteGoals.map(goal => goal.label).join(", ");
  const writingStyle = websiteDetails.websiteWritingStyle
    .map(personality => personality.label)
    .join(", ");
  return `Website Category: ${category} \nWebsite Goals: ${goals} \nWebsite Writing Style: ${writingStyle}`;
}

export function getTaglinePrompt(
  websiteDetails: OnboardingWebsiteDetails
): ChatMessage[] {
  const details = getWebsiteDetailsAsQualifier(websiteDetails);
  return [
    {
      role: "system",
      content: `
          ## Instructions:
          You are an imaginative and adaptable copywriter. Your task is to write a unique, compelling tagline that reflects the tone, personality, and purpose of a specific website.
          This tagline will serve as a sample sentence that defines the language, tone, and style of the website. You will be given structured information about the website, such as its name, category, description, goals, and personality traits.
          Your output must be relevant to the site's identity and stand out from generic phrases.

          ## Guidelines:
          - The tagline must include the website's name somewhere in the sentence.
          - It must reflect the website's category, goals, and personality.
          - It should be between 15 and 20 words.
          - It should not begin with the website name followed by a colon.
          - Avoid clichés and overly generic wording.
          - Match the tone to the personality traits (e.g., bold and playful, sleek and serious, warm and professional).
          - Do not use personality traits or goals provided in the input in the tagline.
          - Sentence case only.
          - Language: English.

          ## Example Input:
          - Website Name: "Tech Innovations"
          - Website Description: "A platform for the latest tech news and reviews."
          - Website Category: "Technology"
          - Website Goals: "Inform, Engage, Inspire"
          - Website Writing Style: "Innovative, Friendly, Professional"

          ## Response format:
          - The response should be in JSON format.
          - The response should include the tagline in a field called "tagline".

          ## Example Output:
          {
            "tagline": "Tech Innovations is your go-to guide for insightful, friendly tech news and cutting-edge reviews."
          }

        `
    },
    {
      role: "user",
      content: details
    }
  ];
}

async function _requestStructuredOutput(
  messages: ChatMessage[],
  json_schema: {}
): Promise<Either<_Error[], string>> {
  return makeWordPressAjaxRequest({
    action: "breakdance_onboarding_request_structured_output",
    friendlyName: "AI Assistant",
    dataToPost: {
      messages: fpJsonStringify(messages),
      json_schema: fpJsonStringify(json_schema)
    },
    decoder: t.string,
    resultKey: "data"
  });
}

export async function generateSitemap(): Promise<Sitemap | undefined> {
  const websiteName = onboardingStore.websiteDetails.websiteName;
  const websiteDescription = onboardingStore.websiteDetails.websiteDescription;
  const pageCount = "5 to 10";
  const sectionCount = "at least 8";

  const sectionTypesString = getSectionTypes()
    .map(sectionType => `'${sectionType}'`)
    .join(", ");

  const result = await requestStructuredOutput(
    `
    ## Sitemap Generation Prompt

    - Generate a sitemap for a website.
    - The website should have ${pageCount} pages.
    - Each page should have ${sectionCount} sections.
    - Website name: ${websiteName}
    - Website description: ${websiteDescription}
    - Website category: ${onboardingStore.websiteDetails.websiteCategory
      .map(category => category.label)
      .join(", ")}
    - Website goals: ${onboardingStore.websiteDetails.websiteGoals
      .map(goal => goal.label)
      .join(", ")}

    ## Sitemap Structure
    
    - Each page is represented as a section tree
    - First page should be "Home" page.
    - Valid values for section type are ${sectionTypesString}.
    - For each section tree, select one item at random from the section library object.
    - For each section tree, the "type" field must be one of the valid section types.
    - Each section tree should start with "Navigation Headers" section.
    - Each section tree should end with "Footers" section.
    - For each page "Navigation Headers" section should have same description. 
    - For each page "Footers" section should have same description.
    - Valid values for section tree are:
  
    .
    `,

    SitemapV
  );

  return result;
}

export async function requestStructuredOutput<A>(
  message: string,
  GuardV: t.Type<A, any, unknown>
): Promise<A | undefined> {
  const ValidGuardV = t.type({
    response: GuardV
  });

  const ajaxRequestResponse = await _requestStructuredOutput(
    [
      {
        role: "user",
        content: message
      }
    ],
    {
      name: "my_schema",
      strict: true,
      schema: ioTsToJsonSchema(ValidGuardV)
    }
  );

  const validated = pipe(
    ajaxRequestResponse,
    fold(
      () => {
        // AJAX returned invalid response
        console.error(
          "AJAX returned an error or invalid response",
          ajaxRequestResponse
        );
        return undefined;
      },
      successfulResponse => {
        console.log("AJAX returned a valid response", successfulResponse);

        const parsedResponse = fpJsonParseTypesafe(
          successfulResponse,
          ValidGuardV
        );

        return pipe(
          parsedResponse,
          fold(
            () => {
              // iots errors
              return undefined;
            },
            validObject => {
              return validObject.response;
            }
          )
        );
      }
    )
  );

  return validated;
}

export function getSectionTypes() {
  return Object.keys(getBarebonesSectionLibrary());
}

export function getSectionTypesAsString() {
  return Object;
}

export async function parseTaglineCompletion(
  prompt: ChatMessage[]
): Promise<TaglineResponseType | null> {
  try {
    // Use streamCompletion to get the response
    const completion = await streamCompletion(prompt);

    if (typeof completion !== "string") {
      throw new Error("Invalid completion response");
    }

    const parsed = fpJsonParseTypesafe(completion, TaglineResponse);

    return pipe(
      parsed,
      fold(
        () => null,
        validResponse => validResponse
      )
    );
  } catch (error) {
    console.error("Error parsing tagline completion:", error);
    throw error;
  }
}

export async function streamCompletion(
  prompt: ChatMessage[]
): Promise<string | boolean> {
  const eventSource = getOnboardingEventSource(prompt);
  let parts = "";

  if (!eventSource) {
    throw new Error("No event source was returned from OpenAI API.");
  }

  return new Promise((resolve, reject) => {
    eventSource.onmessage = async event => {
      if (event.data === "[DONE]") {
        eventSource.close();

        try {
          jsonrepair(parts);
          resolve(parts);
        } catch (error) {
          reject(error);
        }
      } else {
        parts += getTextFromChunk(event.data);
        parts = cleanJson(parts);

        if (!parts) return;

        try {
          const cleanJson = cleanJsonBeforeParse(parts);
          const repaired = jsonrepair(cleanJson);

          pipe(
            fpJsonParse(repaired),
            fold(
              () => false,
              response => response
            )
          );
        } catch (error) {
          console.debug(parts);
          console.debug(error);
        }
      }
    };

    eventSource.onerror = error => {
      console.error("Event source error:", error);
      eventSource.close();
      reject(error);
    };
  });
}

export async function streamRewrite(
  sectionId: TreeNode["id"],
  prompt: ChatMessage[],
  fields: FieldToComplete[]
) {
  const eventSource = getOnboardingEventSource(prompt);
  let parts = "";

  if (!eventSource) {
    throw new Error("No event source was returned from OpenAI API.");
  }

  return new Promise((resolve, reject) => {
    eventSource.onmessage = async event => {
      if (event.data === "[DONE]") {
        eventSource.close();

        try {
          const repaired = jsonrepair(parts);
          const parsedCompletion = processCompletion(repaired, fields);
          updateDocument(parsedCompletion.elements);
          replaceImagesInSection(sectionId, parsedCompletion.unsplash_keywords);
          console.log("%cOutput", "color: green;");
          console.log(repaired);
          resolve(parts);
        } catch (error) {
          reject(error);
        }
      } else {
        parts += getTextFromChunk(event.data);
        parts = cleanJson(parts);

        if (!parts) return;

        try {
          const cleanJson = cleanJsonBeforeParse(parts);
          const repaired = jsonrepair(cleanJson);
          const parsedCompletion = processCompletion(repaired, fields);
          updateDocument(parsedCompletion.elements);
        } catch (error) {
          // Parsing is allowed to fail here, we'll just try again in the next chunk.
          console.debug(parts);
          console.debug(error);
        }
      }
    };

    eventSource.onerror = event => {
      eventSource.close();
      reject((event as MessageEvent).data);
    };
  });
}

export async function replaceImagesInSection(
  sectionId: TreeNode["id"],
  unsplashKeywords: string[]
) {
  const node = findNode(documentStore.document.tree, sectionId);

  if (!node) return;

  const imagesToFill = getImagesToComplete(node);

  console.log(
    `%cReplacing images in section: ${sectionId} with keywords: ${unsplashKeywords}`,
    "color: red; font-weight: bold;"
  );

  const imagesFromUnsplash = await callImageService({
    query: unsplashKeywords.join(", "),
    page: 1,
    per_page: imagesToFill.length
  }, new AbortController());
  
  updateImages(imagesToFill, imagesFromUnsplash, false);
}

export async function rewriteContentInSection(sectionId: TreeNode["id"]) {
  console.log(
    `%cRewriting content in section: ${sectionId}`,
    "color: red; font-weight: bold;"
  );
  const node = findNode(documentStore.document.tree, sectionId);

  if (!node) return;

  const promptInfo = getPromptInfoFromNode(node);

  promptInfo.onboarding.shouldGenerateOnInitialLoad = false;

  const fields = getFieldsToComplete(node);
  const fieldsToFill = formatFieldsForOpenAI(fields);
  const prompt = getPrompt(
    fieldsToFill,
    promptInfo,
    globalStore.aiSettings.prompt
  );

  saveToElementMetadata(node, promptInfo);

  if (!fields.length) return;

  try {
    setElementLoadingState(fields, true);
    await streamRewrite(node.id, prompt, fields);
    setElementLoadingState(fields, false);
  } catch (error) {
    handleAiError(error as string);
  }
}

export function setElementLoadingState(
  fields: FieldToComplete[],
  isUpdating: boolean
) {
  fields.forEach(field => {
    uiStore.setElementUpdatingState({
      elementId: field.nodeId,
      isUpdating
    });
  });
}

export function getPromptInfoFromNode(node: TreeNode): PromptInfo {
  const shouldGenerateOnInitialLoad =
    !!node.data.properties?.meta?.promptInfo?.onboarding
      ?.shouldGenerateOnInitialLoad;
  const sectionType =
    node.data.properties?.meta?.promptInfo?.onboarding?.sectionType ?? false;

  return {
    sectionDescription: getElementDescription(node),
    qualifiers: getElementQualifier(node),
    temperature: getElementTemperature(node),
    lockedFields: getLockedFieldsPath(node),
    locale: globalStore.aiSettings.locale,
    unsplash: {
      enabled: false,
      fillEmptyImagesOnly: false,
      keywords: ""
    },
    onboarding: {
      shouldGenerateOnInitialLoad: shouldGenerateOnInitialLoad,
      sectionType: sectionType
    },
    singularity: {
      shouldGenerateOnInitialLoad: shouldGenerateOnInitialLoad,
      sectionData: {
        type: sectionType,
        index: false
      },
      unsplashKeywords: []
    }
  };
}

export function maybeRegisterStoreActionsForOnboardingSectionGeneration() {
  if (!window.Breakdance.isOnboarding) return;
  breakdanceStoreActions.register.onMountedElement((args: OnMountedElement) => {
    if (!args.elementId) return;

    const node = findNode(documentStore.document.tree, args.elementId);

    if (!node) return;

    if (node.data.type !== SECTION_SLUG) return;

    const promptInfo = getPromptInfoFromNode(node);

    if (promptInfo.onboarding.shouldGenerateOnInitialLoad) {
      rewriteContentInSection(node.id);
    }
  });
}

export function getElementOnboaradingData(node: TreeNode): {
  sectionType: string | false;
  shouldGenerateOnInitialLoad: boolean;
} {
  const onboardingData = getElementPrompt(node)?.onboarding;

  if (onboardingData && onboardingData?.sectionType) {
    return {
      sectionType: onboardingData.sectionType ?? false,
      shouldGenerateOnInitialLoad:
        onboardingData.shouldGenerateOnInitialLoad || false
    };
  }

  return {
    sectionType: false,
    shouldGenerateOnInitialLoad: false
  };
}

export function getOnboardingEventSource(prompt: ChatMessage[]) {
  const endpoint =
    "https://breakdance.com/wp-json/breakdance/v1/onboarding/ai/stream-completion";

  const promptString = jsonStringifyOrUndefined(prompt);

  if (!promptString) {
    return null;
  }

  const serializedPrompt = encodeURIComponent(promptString);
  const url = `${endpoint}?messages=${serializedPrompt}`;

  return new EventSource(url);
}
